/**
 * Screening Info Requests — list page (Angular "screening-info-requests-config").
 * Client-side list: load-all, submit-search, client pagination, HTML5 drag-reorder.
 * Add / Edit via modal; delete via confirm dialog. Message column renders sanitized HTML.
 */

import { useState } from 'react'
import { GripVertical, Pencil, Trash2 } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ListingDataTableCard, LISTING_DATA_TABLE_CLASS } from '@/components/ui/listingDataTable'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import { cn } from '@/lib/utils/helpers'
import { ScreeningInfoRequestEditModal } from '../components/ScreeningInfoRequestEditModal'
import { useReorderableClientList } from '../hooks/useReorderableClientList'
import { BUSINESS_CONFIG_PER_PAGE_OPTIONS } from '../constants'
import {
  deleteScreeningInfoRequest,
  getScreeningInfoRequests,
  reorderScreeningInfoRequests,
} from '../services/screeningInfoRequestsService'
import { sanitizeHtml } from '../utils/businessConfigHelpers'
import type { ScreeningInfoRequest } from '../types'

const getScreeningId = (item: ScreeningInfoRequest): number => item.screening_info_request_id
const screeningMatchesSearch = (item: ScreeningInfoRequest, query: string): boolean =>
  (item.section_label || '').toLowerCase().includes(query) ||
  (item.message_text || '').toLowerCase().includes(query)

export function ScreeningInfoRequestsListPage() {
  const list = useReorderableClientList<ScreeningInfoRequest>({
    fetchAll: getScreeningInfoRequests,
    getId: getScreeningId,
    matchesSearch: screeningMatchesSearch,
    persistOrder: reorderScreeningInfoRequests,
    reorderSuccessMessage: 'Order updated.',
    searchGuardMessage: 'Clear search before reordering messages.',
  })

  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ScreeningInfoRequest | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ScreeningInfoRequest | null>(null)

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }
  const openEdit = (item: ScreeningInfoRequest) => {
    setEditTarget(item)
    setEditOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteScreeningInfoRequest(deleteTarget.screening_info_request_id)
      showSuccess('Message removed.')
      setDeleteTarget(null)
      void list.reload()
    } catch (error) {
      showApiError(error)
    }
  }

  return (
    <PageShell
      title="SCREENING INFO REQUESTS"
      headerAction={
        <button type="button" onClick={openAdd} className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}>
          Add
        </button>
      }
    >
      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          list.submitSearch()
        }}
      >
        <input
          type="text"
          value={list.searchInput}
          onChange={(event) => list.setSearchInput(event.target.value)}
          placeholder="Search.."
          className="min-w-0 flex-1 sm:max-w-xs px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button type="submit" disabled={list.loading} className={cn(primaryEntityActionButtonClass)}>
          Search
        </button>
        <button type="button" onClick={list.resetSearch} className={cn(secondaryEntityActionButtonClass)}>
          Reset
        </button>
      </form>

      <ListingDataTableCard>
        <div className="w-full">
          <table className={cn(parityStyles.propertyListingTable, parityStyles.listingTable, LISTING_DATA_TABLE_CLASS)}>
            <thead className="border-b border-[#c8d7e8] bg-[#dfeaf5] text-slate-900">
              <tr>
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-16">S.No.</th>
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-16">Reorder</th>
                <th className="px-2 py-2 !text-left text-xs sm:px-4 sm:py-3 sm:text-sm w-1/5">Section</th>
                <th className="px-2 py-2 !text-left text-xs sm:px-4 sm:py-3 sm:text-sm">Message</th>
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-32">Personalized Note</th>
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-28">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#dfe7f0] [&>tr:nth-child(even)]:bg-[#f2f7fd]">
              {list.loading ? (
                <ListingTableLoadingRow colSpan={6} />
              ) : list.pageItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-500 sm:px-4 sm:py-8">
                    No Records Found. Please try a different Search or Filter from the drop-downs above.
                  </td>
                </tr>
              ) : (
                list.pageItems.map((item, index) => (
                  <tr
                    key={item.screening_info_request_id}
                    className={cn(
                      parityStyles.listingTableRow,
                      list.dnd.draggingId === item.screening_info_request_id && 'opacity-40',
                    )}
                    onDragOver={list.dnd.onRowDragOver}
                    onDragEnter={list.dnd.onRowDragEnter(index)}
                    onDrop={list.dnd.onRowDrop}
                  >
                    <td className="px-2 py-2 text-center text-sm text-slate-700 sm:px-4 sm:py-3">
                      {list.pageStart + index + 1}
                    </td>
                    <td className="px-2 py-2 text-center sm:px-4 sm:py-3">
                      <button
                        type="button"
                        draggable
                        onDragStart={list.dnd.onGripDragStart(index)}
                        onDragEnd={list.dnd.onGripDragEnd}
                        className="cursor-grab p-1.5 rounded text-slate-500 hover:bg-slate-100 active:cursor-grabbing"
                        aria-label="Drag to reorder"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="px-2 py-2 text-sm font-medium text-slate-900 sm:px-4 sm:py-3 sm:text-base">
                      {item.section_label}
                    </td>
                    <td className="px-2 py-2 text-sm text-slate-700 sm:px-4 sm:py-3 sm:text-base">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.message_text) }}
                      />
                    </td>
                    <td className="px-2 py-2 text-center text-sm text-slate-700 sm:px-4 sm:py-3">
                      {Number(item.allow_personalized_note) ? 'Yes' : 'No'}
                    </td>
                    <td className="px-2 py-2 sm:px-4 sm:py-3">
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                          aria-label="Edit"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          aria-label="Remove"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </ListingDataTableCard>

      {list.totalRecords > 0 && (
        <ListPaginationBar
          placement="footer"
          testIdPrefix="screening-info-requests"
          currentPage={list.page}
          lastPage={list.lastPage}
          totalRecords={list.totalRecords}
          perPage={list.perPage}
          perPageOptions={BUSINESS_CONFIG_PER_PAGE_OPTIONS}
          onPageChange={list.setPage}
          onPerPageChange={list.changePerPage}
        />
      )}

      <ScreeningInfoRequestEditModal
        isOpen={editOpen}
        target={editTarget}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSaved={() => {
          setEditOpen(false)
          setEditTarget(null)
          void list.reload()
        }}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Confirm"
        message="Do you want to remove this message option? Existing notes and emails are not changed."
        primaryLabel="Yes"
        onPrimary={confirmDelete}
        secondaryLabel="No"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </PageShell>
  )
}
